/**
 * Model 2 API Service
 * Handles all notebook, model, source, and chat operations for Model 2
 */

export const API_BASE_URL = "https://ddre.kendal.ai";

// --- Types ---
export interface Notebook {
  id: string;
  name: string;
  description: string;
  archived: boolean;
  created: string;
  updated: string;
  source_count: number;
  note_count: number;
}

export interface Model {
  id: string;
  name: string;
  provider: string;
  type: "language" | "embedding";
  created: string;
  updated: string;
}

export interface ModelDefaults {
  default_chat_model?: string;
  default_embedding_model?: string;
  default_transformation_model?: string;
}

export interface Source {
  id: string;
  title: string;
  topics: string[];
  asset: {
    file_path: string;
    url: string;
  } | null;
  embedded: boolean;
  embedded_chunks: number;
  insights_count?: number;
  created: string;
  updated: string;
  file_available: boolean | null;
  command_id: string;
  status: "new" | "processing" | "completed" | "error";
  processing_info: {
    async: boolean;
    queued?: boolean;
    started_at?: string;
    completed_at?: string;
    error?: string;
  };
  notebooks?: string[] | null;
}

export interface ChatSession {
  id: string;
  title: string;
  notebook_id: string;
  created: string;
  updated: string;
  message_count: number;
  model_override: string | null;
  messages?: ChatMessage[];
}

export interface ChatMessage {
  id: string;
  type: "human" | "ai";
  content: string;
  timestamp: string | null;
}

export interface SourceInsight {
  id: string;
  created: string | null;
  updated: string | null;
  insight_type: string;
  content: string;
}

export interface SourceWithInsights {
  id: string;
  title: string;
  insights: SourceInsight[];
}

export interface ChatContext {
  context: {
    sources: SourceWithInsights[];
    notes: any[];
  };
  token_count: number;
  char_count: number;
}

export interface ChatExecuteRequest {
  session_id: string;
  message: string;
  context: ChatContext["context"];
}

export interface ChatExecuteResponse {
  session_id: string;
  messages: ChatMessage[];
}

// --- Notebook APIs ---
export const createNotebook = async (
  name: string,
  description: string,
): Promise<Notebook> => {
  const response = await fetch(`${API_BASE_URL}/api/notebooks`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name, description }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create notebook: ${response.statusText}`);
  }

  return response.json();
};

export const getNotebooks = async (): Promise<Notebook[]> => {
  const response = await fetch(
    `${API_BASE_URL}/api/notebooks?order_by=updated%20desc`,
    {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch notebooks: ${response.statusText}`);
  }

  return response.json();
};

// --- Model APIs ---
export const createModel = async (
  type: "language" | "embedding",
  name: string,
  provider: string,
): Promise<Model> => {
  const response = await fetch(`${API_BASE_URL}/api/models`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ type, name, provider }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create model: ${response.statusText}`);
  }

  return response.json();
};

export const getModels = async (): Promise<Model[]> => {
  const response = await fetch(`${API_BASE_URL}/api/models`, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch models: ${response.statusText}`);
  }

  return response.json();
};

export const setDefaultModels = async (
  defaults: ModelDefaults,
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/models/defaults`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(defaults),
  });

  if (!response.ok) {
    throw new Error(`Failed to set default models: ${response.statusText}`);
  }
};

export const setDefaultTransformationModel = async (
  transformationModelId: string,
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/models/defaults`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      default_transformation_model: transformationModelId,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to set default transformation model: ${response.statusText}`,
    );
  }
};

// --- Transformation APIs ---
export const getTransformations = async () => {
  const response = await fetch(`${API_BASE_URL}/api/transformations`, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch transformations: ${response.statusText}`);
  }

  return response.json();
};

// --- Source APIs ---
export const uploadSource = async (
  file: File,
  notebookIds: string[],
  transformationIds: string[] = [],
  embed: boolean = true,
  asyncProcessing: boolean = true,
): Promise<Source> => {
  const formData = new FormData();
  formData.append("type", "upload");
  formData.append("notebooks", JSON.stringify(notebookIds));
  formData.append("file", file);
  formData.append("embed", embed ? "true" : "false");
  formData.append("delete_source", "false");
  formData.append("async_processing", asyncProcessing ? "true" : "false");

  // Add transformations
  transformationIds.forEach((id) => {
    formData.append("transformations", JSON.stringify([id]));
  });

  const response = await fetch(`${API_BASE_URL}/api/sources`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload source: ${response.statusText}`);
  }

  return response.json();
};

export const getSources = async (
  limit: number = 50,
  offset: number = 0,
): Promise<Source[]> => {
  const response = await fetch(
    `${API_BASE_URL}/api/sources?limit=${limit}&offset=${offset}&sort_by=updated&sort_order=desc`,
    {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch sources: ${response.statusText}`);
  }

  return response.json();
};

export const getSource = async (sourceId: string): Promise<Source> => {
  const response = await fetch(
    `${API_BASE_URL}/api/sources/${encodeURIComponent(sourceId)}`,
    {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch source: ${response.statusText}`);
  }

  return response.json();
};

export const deleteSource = async (sourceId: string): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/api/sources/${encodeURIComponent(sourceId)}`,
    {
      method: "DELETE",
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to delete source: ${response.statusText}`);
  }
};

// --- Chat Context APIs ---
export const getChatContext = async (
  notebookId: string,
  sources: { [key: string]: string },
  notes: any = {},
): Promise<ChatContext> => {
  const response = await fetch(`${API_BASE_URL}/api/chat/context`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      notebook_id: notebookId,
      context_config: {
        sources,
        notes,
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get chat context: ${response.statusText}`);
  }

  return response.json();
};

// --- Chat Session APIs ---
export const createChatSession = async (
  notebookId: string,
  title: string,
): Promise<ChatSession> => {
  const response = await fetch(`${API_BASE_URL}/api/chat/sessions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      notebook_id: notebookId,
      title,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create chat session: ${response.statusText}`);
  }

  return response.json();
};

export const getChatSessions = async (
  notebookId: string,
): Promise<ChatSession[]> => {
  const response = await fetch(
    `${API_BASE_URL}/api/chat/sessions?notebook_id=${encodeURIComponent(notebookId)}`,
    {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch chat sessions: ${response.statusText}`);
  }

  return response.json();
};

export const getChatSession = async (
  sessionId: string,
): Promise<ChatSession> => {
  const response = await fetch(
    `${API_BASE_URL}/api/chat/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch chat session: ${response.statusText}`);
  }

  return response.json();
};

export const clearChatSessionMessages = async (
  sessionId: string,
): Promise<void> => {
  const attempts = [
    {
      url: `${API_BASE_URL}/api/chat/sessions/${encodeURIComponent(sessionId)}/messages`,
      method: "DELETE",
    },
    {
      url: `${API_BASE_URL}/api/chat/sessions/${encodeURIComponent(sessionId)}/messages/clear`,
      method: "POST",
    },
    {
      url: `${API_BASE_URL}/api/chat/sessions/${encodeURIComponent(sessionId)}/clear`,
      method: "POST",
    },
    {
      url: `${API_BASE_URL}/api/chat/sessions/${encodeURIComponent(sessionId)}/clear`,
      method: "DELETE",
    },
    {
      url: `${API_BASE_URL}/api/chat/sessions/${encodeURIComponent(sessionId)}/reset`,
      method: "POST",
    },
  ] as const;

  let lastError = "Unknown error";
  for (const attempt of attempts) {
    const response = await fetch(attempt.url, { method: attempt.method });
    if (response.ok) return;
    lastError = response.statusText || `${response.status}`;
  }

  throw new Error(`Failed to clear chat session: ${lastError}`);
};

export const deleteChatSession = async (sessionId: string): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/api/chat/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: "DELETE",
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to delete chat session: ${response.statusText}`);
  }
};

// --- Chat Execute API ---
export const executeChatQuery = async (
  request: ChatExecuteRequest,
): Promise<ChatExecuteResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/chat/execute`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`Failed to execute chat query: ${response.statusText}`);
  }

  return response.json();
};

// --- Initialization Helper ---
export const initializeModel2 = async (): Promise<{
  notebookId: string;
  chatModelId: string;
  embeddingModelId: string;
}> => {
  try {
    // Get or create notebook
    let notebooks = await getNotebooks();
    let notebook = notebooks.find((n) => n.name === "DDRE Dashboard");

    if (!notebook) {
      notebook = await createNotebook(
        "DDRE Dashboard",
        "Dashboard for real estate analysis with dual model integration",
      );
    }

    // Get or create models
    let models = await getModels();
    let chatModel = models.find(
      (m) => m.name === "gpt-5-chat-latest" && m.type === "language",
    );
    let embeddingModel = models.find(
      (m) => m.name === "text-embedding-3-small" && m.type === "embedding",
    );

    if (!chatModel) {
      chatModel = await createModel("language", "gpt-5-chat-latest", "openai");
    }

    if (!embeddingModel) {
      embeddingModel = await createModel(
        "embedding",
        "text-embedding-3-small",
        "openai",
      );
    }

    // Set default models
    await setDefaultModels({
      default_chat_model: chatModel.id,
      default_embedding_model: embeddingModel.id,
    });

    // Set transformation model to same as chat model
    await setDefaultTransformationModel(chatModel.id);

    return {
      notebookId: notebook.id,
      chatModelId: chatModel.id,
      embeddingModelId: embeddingModel.id,
    };
  } catch (error) {
    console.error("Failed to initialize Model 2:", error);
    throw error;
  }
};
